/**
 * Link to Work Order Dialog
 *
 * Allows linking inspection recommendations to work order items.
 * Can create a new work order item or link to an existing one.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Radio,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BuildIcon from '@mui/icons-material/Build';
import type { WorkOrderItem } from '@/hooks/useWorkOrders';
import { describeApiError } from '@/lib/describeApiError';

interface LinkToWorkOrderDialogProps {
  open: boolean;
  onClose: () => void;
  recommendation: {
    id: string;
    description: string;
    urgency: 'immediate' | 'soon' | 'future';
    estimatedPriceCents: number;
    workOrderItemId?: string | null;
    linkedAt?: string;
  } | null;
  workOrderNumber: string;
  existingItems?: WorkOrderItem[];
  onLinkToExisting: (recId: string, workOrderItemId: string) => Promise<void>;
  onCreateAndLink: (recId: string, recommendation: {
    description: string;
    partsCostCents: number;
    laborCostCents: number;
    laborHours?: number;
  }) => Promise<void>;
  onUnlink: (recId: string) => Promise<void>;
  isLoading?: boolean;
}

export function LinkToWorkOrderDialog({
  open,
  onClose,
  recommendation,
  workOrderNumber,
  existingItems = [],
  onLinkToExisting,
  onCreateAndLink,
  onUnlink,
  isLoading = false,
}: LinkToWorkOrderDialogProps) {
  const { t } = useTranslation('inspection');
  const { t: tc } = useTranslation('common');
  const theme = useTheme();
  const [selectedItemId, setSelectedItemId] = useState<string>('new');

  const urgencyConfig = {
    immediate: { label: t('linkDialog.urgent'), color: theme.palette.error.main },
    soon: { label: t('linkDialog.soon'), color: theme.palette.warning.main },
    future: { label: t('linkDialog.noRush'), color: theme.palette.success.main },
  };
  const [error, setError] = useState<string | null>(null);

  if (!recommendation) return null;

  const formatPrice = (cents: number) => `€${(cents / 100).toFixed(2)}`;

  const isAlreadyLinked = !!recommendation.workOrderItemId;
  const linkedItem = existingItems.find(
    (item) => item.id === recommendation.workOrderItemId
  );

  // Filter out items that are already linked to other recommendations
  const availableItems = existingItems.filter(
    (item) => item.id === recommendation.workOrderItemId || item.status === 'pending'
  );

  const handleLink = async () => {
    setError(null);

    try {
      if (selectedItemId === 'new') {
        // Estimate: 60% parts, 40% labor (rough split)
        const partsCostCents = Math.round(recommendation.estimatedPriceCents * 0.4);
        const laborCostCents = recommendation.estimatedPriceCents - partsCostCents;

        await onCreateAndLink(recommendation.id, {
          description: recommendation.description,
          partsCostCents,
          laborCostCents,
          laborHours: 60, // Default 1 hour
        });
      } else {
        await onLinkToExisting(recommendation.id, selectedItemId);
      }
      onClose();
    } catch (err) {
      setError(describeApiError(err, tc, { fallback: t('linkDialog.linkFailed') }));
    }
  };

  const handleUnlink = async () => {
    setError(null);

    try {
      await onUnlink(recommendation.id);
      onClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('linkDialog.unlinkFailed')
      );
    }
  };

  return (
    <ResponsiveDialog mobileTitle={t('linkDialog.linkToWorkOrder')} open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            {isAlreadyLinked ? t('linkDialog.workOrderLinked') : t('linkDialog.linkToWorkOrder')}
          </Typography>
        </Box>
        <Tooltip title={tc('close')}>
          <span>
            <IconButton onClick={onClose} size="small" disabled={isLoading} aria-label={tc('close')}>
              <CloseIcon />
            </IconButton>
          </span>
        </Tooltip>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setError(null); }}>
            {error}
          </Alert>
        )}

        {/* Recommendation Details */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('linkDialog.inspectionFinding')}
          </Typography>
          <Typography variant="body1" fontWeight={500} sx={{ mb: 1 }}>
            {recommendation.description}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              size="small"
              label={urgencyConfig[recommendation.urgency].label}
              sx={{
                backgroundColor: alpha(urgencyConfig[recommendation.urgency].color, 0.08),
                color: urgencyConfig[recommendation.urgency].color,
                fontWeight: 500,
              }}
            />
            <Typography variant="body2" fontWeight={600}>
              {formatPrice(recommendation.estimatedPriceCents)}
            </Typography>
          </Box>
        </Paper>

        {/* Work Order Reference */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('linkDialog.workOrder')}
          </Typography>
          <Typography variant="body2">
            {workOrderNumber}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {isAlreadyLinked ? (
          // Already linked - show linked item details
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2">
                {t('linkDialog.findingLinkedToLine')}
              </Typography>
            </Alert>

            {linkedItem && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CheckCircleIcon color="success" fontSize="small" />
                  <Typography variant="subtitle2">{t('linkDialog.linkedLine')}</Typography>
                </Box>
                <Typography variant="body2" fontWeight={500}>
                  {linkedItem.description}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('linkDialog.parts')}: {formatPrice(linkedItem.partsCostCents)} | {t('linkDialog.labor')}:{' '}
                  {formatPrice(linkedItem.laborCostCents)}
                </Typography>
                <Chip
                  size="small"
                  label={linkedItem.status === 'approved' ? t('linkDialog.approved') :
                         linkedItem.status === 'completed' ? t('linkDialog.completed') :
                         linkedItem.status === 'rejected' ? t('linkDialog.rejected') : t('linkDialog.pending')}
                  color={linkedItem.status === 'approved' ? 'success' :
                         linkedItem.status === 'completed' ? 'info' :
                         linkedItem.status === 'rejected' ? 'error' : 'warning'}
                  sx={{ mt: 1 }}
                />
              </Paper>
            )}
          </Box>
        ) : (
          // Not linked - show linking options
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('linkDialog.selectAction')}
            </Typography>

            <List disablePadding>
              {/* Create new item option */}
              <ListItemButton
                selected={selectedItemId === 'new'}
                onClick={() => { setSelectedItemId('new'); }}
                sx={{
                  border: 1,
                  borderColor: selectedItemId === 'new' ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <ListItemIcon>
                  <Radio checked={selectedItemId === 'new'} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AddIcon fontSize="small" color="primary" />
                      <Typography variant="body2" fontWeight={500}>
                        {t('linkDialog.createNewLine')}
                      </Typography>
                    </Box>
                  }
                  secondary={t('linkDialog.createNewLineDescription')}
                />
              </ListItemButton>

              {/* Existing items */}
              {availableItems.length > 0 && (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 2, mb: 1 }}
                  >
                    {t('linkDialog.orLinkToExisting')}
                  </Typography>
                  {availableItems.map((item) => (
                    <ListItemButton
                      key={item.id}
                      selected={selectedItemId === item.id}
                      onClick={() => { setSelectedItemId(item.id); }}
                      sx={{
                        border: 1,
                        borderColor: selectedItemId === item.id ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        mb: 1,
                      }}
                    >
                      <ListItemIcon>
                        <Radio checked={selectedItemId === item.id} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BuildIcon fontSize="small" color="action" />
                            <Typography variant="body2" fontWeight={500}>
                              {item.description}
                            </Typography>
                          </Box>
                        }
                        secondary={`${t('linkDialog.parts')}: ${formatPrice(item.partsCostCents)} | ${t('linkDialog.labor')}: ${formatPrice(item.laborCostCents)}`}
                      />
                    </ListItemButton>
                  ))}
                </>
              )}
            </List>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {isAlreadyLinked ? (
          <>
            <Button
              variant="outlined"
              color="error"
              startIcon={<LinkOffIcon />}
              onClick={() => { void handleUnlink(); }}
              disabled={isLoading}
            >
              {t('linkDialog.removeLink')}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" onClick={onClose} disabled={isLoading}>
              {tc('close')}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={isLoading}>
              {tc('cancel')}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={isLoading ? <CircularProgress size={16} /> : <LinkIcon />}
              onClick={() => { void handleLink(); }}
              disabled={isLoading}
            >
              {selectedItemId === 'new' ? t('linkDialog.createAndLink') : t('linkDialog.link')}
            </Button>
          </>
        )}
      </DialogActions>
    </ResponsiveDialog>
  );
}
